import { useStore, useStoreMap } from 'effector-react'
import {
  $dealersShowMoreCars,
  $declaration,
  $disabledFilterValues,
  FilterFieldCheckbox,
  FilterFieldColorSelect,
  FilterFieldRange,
  FilterFieldSwitch,
  FilterGroup as FilterGroupType,
  FilterSectionType,
  FilterValue,
  mergeValues,
  removeValues,
  setRelatedFiltersInsteadDeclaration
} from 'features/filter/model'
import { $filterValues, replaceValues } from 'features/filter/model'
import React, { FC, useMemo } from 'react'
import styled from 'styled-components'
import { Checkbox } from 'ui/checkbox'
import { ColorSelect } from 'ui/color-select'
import { RangeSlider } from 'ui/range-slider'
import { Switch } from 'ui/switch'

import { compareFiltersLength } from '../../../lib/compare-filter-length'
import { getProjectName } from '../../../lib/get-project-name'
import { setCarsNextDealer } from '../../../lib/show-cars-next-dealer'
import { useAVNConfig } from '../../../stages/AVN'
import { $isLoadMoreCars, $relatedFilter, setFirstLoadPage } from '../../cars-list/model'

type Props = {
  groups: FilterGroupType[]
  sectionType: FilterSectionType
  sectionId: string
  showCarsNextDealer: boolean
}

export const FilterGroups: FC<Props> = ({ groups, sectionType, sectionId, showCarsNextDealer }) => {
  const dealersShowMoreCarsStore = useStore($dealersShowMoreCars)
  const isLoadMoreCars = useStore($isLoadMoreCars)
  const relatedFilters = useStore($relatedFilter)
  const declaration = useStore($declaration)

  const sectionValue = useStoreMap({
    store: $filterValues,
    keys: [sectionId],
    fn: (store, [sectionId]) => (store ? store[sectionId] : [])
  })

  const disabledValues = useStoreMap({
    store: $disabledFilterValues,
    keys: [sectionId],
    fn: (store, [sectionId]) => (store ? store[sectionId] : [])
  })

  const activeGroups = useMemo(() => {
    if (disabledValues) {
      return groups.filter((group) => {
        // @ts-ignore
        return !group.fields.every((field) => disabledValues.includes(field.value))
      })
    }
    return []
  }, [disabledValues])

  const replaceSectionValue = (value: FilterValue) => {
    if (showCarsNextDealer) {
      setCarsNextDealer(dealersShowMoreCarsStore.showCarsNextDealer, value[0]?.toString())
    }
    replaceValues({ [sectionId]: value })
  }

  const mergeSectionValue = (value: FilterValue) => {
    if (showCarsNextDealer) {
      setCarsNextDealer(dealersShowMoreCarsStore.showCarsNextDealer, value[0]?.toString())
    }
    return mergeValues({ [sectionId]: value })
  }

  const removeSectionValue = (value: FilterValue) => {
    if (showCarsNextDealer) {
      setCarsNextDealer(dealersShowMoreCarsStore.showCarsNextDealer, value[0]?.toString())
    }
    return removeValues({ [sectionId]: value })
  }
  const handleCheckboxChange = (value: string) => (checked: boolean) => {
    if (compareFiltersLength(declaration, relatedFilters)) {
      setRelatedFiltersInsteadDeclaration(relatedFilters)
    }

    setFirstLoadPage(false)
    checked ? mergeSectionValue([value]) : removeSectionValue([value])
  }
  const handleCheckboxRangeChange = (min: number, max: number) => (checked: boolean) => {
    setFirstLoadPage(false)
    checked ? mergeSectionValue([min, max]) : removeSectionValue([min, max])
  }

  const handleRangeChange = ({ from, to }: { from: number; to: number }) => {
    return mergeSectionValue([from, to])
  }

  const { isShowTooltipInFilter, filterOptions } = useAVNConfig()

  return (
    <>
      {activeGroups.map((group, index) => {
        if (sectionType === 'checkbox') {
          return (
            <Group key={index}>
              {group.title && <GroupTitle>{group.title}</GroupTitle>}
              <GroupFields>
                {(group.fields as FilterFieldCheckbox[]).map((item, index) => {
                  return (
                    <Field key={index}>
                      <Checkbox
                        label={item?.label}
                        onChange={handleCheckboxChange(item?.value)}
                        checked={sectionValue.includes(item?.value)}
                        key={index}
                        name={sectionId}
                        disabled={item?.disabled}
                        styleTank="styleTank"
                      />
                    </Field>
                  )
                })}
              </GroupFields>
            </Group>
          )
        }

        if (sectionType === 'switch') {
          return (
            <Group key={index}>
              {group.title && <GroupTitle>{group.title}</GroupTitle>}
              <GroupFields>
                {(group.fields as FilterFieldSwitch[]).map(({ value, label }, index) => {
                  return (
                    <Field key={index}>
                      <Switch
                        label={label}
                        onChange={handleCheckboxChange(value)}
                        checked={sectionValue.includes(value)}
                        key={index}
                        name={sectionId}
                        tooltip={isShowTooltipInFilter ? group.fields[index]?.tooltip : ''}
                      />
                    </Field>
                  )
                })}
              </GroupFields>
            </Group>
          )
        }

        if (sectionType === 'range') {
          if (sectionId === 'mileage' && filterOptions.data.viewRange === 'checkbox') {
            return (group.fields as FilterFieldRange[]).map(({ min, max, step }, index) => {
              return (
                <div key={sectionId}>
                  <Field key={index}>
                    <Checkbox
                      label={'до 30 000'}
                      onChange={handleCheckboxRangeChange(min, 30000)}
                      checked={sectionValue.includes(30000) && sectionValue.includes(min)}
                      key={index}
                      name={sectionId}
                    />
                  </Field>
                  <Field key={index}>
                    <Checkbox
                      label={'до 100 000'}
                      onChange={handleCheckboxRangeChange(min, 100000)}
                      checked={sectionValue.includes(100000) && sectionValue.includes(min)}
                      key={index}
                      name={sectionId}
                    />
                  </Field>
                  <Field key={index}>
                    <Checkbox
                      label={'больше 100 000'}
                      onChange={handleCheckboxRangeChange(100000, max)}
                      checked={sectionValue.includes(max) && sectionValue.includes(100000)}
                      key={index}
                      name={sectionId}
                    />
                  </Field>
                </div>
              )
            })
          } else {
            return (group.fields as FilterFieldRange[]).map(({ min, max, step }, index) => {
              const value =
                sectionValue[0] && sectionValue[1]
                  ? { from: sectionValue[0] as number, to: sectionValue[1] as number }
                  : undefined
              return (
                <RangeSlider
                  onSubmit={handleRangeChange}
                  min={min}
                  max={max}
                  step={step}
                  key={index}
                  value={value}
                  name={sectionId}
                />
              )
            })
          }
        }

        if (sectionType === 'color-select') {
          const colors = (group?.fields as FilterFieldColorSelect[]).map(
            ({ primaryColor, secondaryColor, colorName, value, disabled = false }) => {
              return { color: primaryColor, secondaryColor, colorName, value, sectionId, disabled }
            }
          )
          return (
            <ColorSelect colors={colors} value={sectionValue as string[]} onChange={replaceSectionValue} key={index} />
          )
        }

        return <div>Form Control Not Implemented</div>
      })}
      {activeGroups.length === 0 && <NoFilterText>Нет подходящих фильтров</NoFilterText>}
    </>
  )
}

const Field = styled.div`
  display: flex;
  &:not(:last-child) {
    margin-bottom: ${({ theme }) => theme.spacing[3]};
  }
`

const GroupFields = styled.div`
  display: flex;
  flex-direction: column;
`

const GroupTitle = styled.div`
  margin-top: 0;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  height: auto;
  color: ${({ theme }) => theme.palette.grey[900]};
  ${({ theme }) => theme.textStyles.p2};
  font-weight: 'bold';

  ${({ theme }) =>
    getProjectName() === 'tank' &&
    `
    color: ${theme.palette.white};
  `}

  ${({ theme }) =>
    getProjectName() === 'haval' &&
    `
    font-weight: 400;
  `}

  ${({ theme }) =>
    getProjectName() === 'generic_v2' &&
    `
    font-weight: 600;
    color: ${theme.palette.grey[100]};
    padding-top: 10px;
  `}
`

const Group = styled.div`
  &:not(:last-child) {
    margin-bottom: ${({ theme }) => theme.spacing[4]};
  }
`

const NoFilterText = styled.div`
  ${({ theme }) => theme.textStyles.p2}
  color: ${({ theme }) => theme.palette.grey[400]}
`
